import { useState, useEffect } from "react";
import type { ShouldUpdateAppResponse, AppUpdateCheck } from "../../sw/types";
import { shouldUpdateApp as shouldUpdateAppClient } from "../../sw/swClient";

/**
 * Hook to check if an app needs to be updated in the service worker cache
 */
export const useShouldUpdateApp = (
  updateCheck: AppUpdateCheck | undefined,
  shouldUpdateAppFunction: typeof shouldUpdateAppClient = shouldUpdateAppClient,
): {
  response: ShouldUpdateAppResponse | undefined;
  isLoading: boolean;
  error: Error | null;
} => {
  const [response, setResponse] = useState<ShouldUpdateAppResponse | undefined>(
    undefined,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let isActive = true;

    const checkShouldUpdate = async () => {
      // Only run if updateCheck is provided
      if (!updateCheck) {
        setResponse(undefined);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const result = await shouldUpdateAppFunction(
          updateCheck.baseUrl,
          updateCheck.project,
          updateCheck.appId,
          updateCheck.lastUpdatedTime,
        );

        if (isActive) {
          setResponse(result);
        }
      } catch (err) {
        if (isActive) {
          setResponse(undefined);
          setError(err instanceof Error ? err : new Error(String(err)));
        }
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    checkShouldUpdate();

    return () => {
      isActive = false;
    };
  }, [updateCheck, shouldUpdateAppFunction]);

  return {
    response,
    isLoading,
    error,
  };
};
